import createPalette from '@mui/material/styles/createPalette';
import { createTheme, ThemeOptions } from '@mui/material/styles';
import { STUDENT_APP_BUTTON_SIZES, STUDENT_APP_DEFAULT_PADDING } from '../types';
import { DISABLED_OPACITY } from '@monorepo/shared/src/styles/sharedStyles';

// Using module augmentation to add an extra property to a color
// source: https://material-ui.com/guides/typescript/#customization-of-theme
declare module '@mui/material/styles/createPalette' {
  interface SimplePaletteColorOptions {
    lighter?: string; // new
    light?: string;
    main: string;
    dark?: string;
    contrastText?: string;
  }

  interface PaletteColor {
    lighter: string; // new
    light: string;
    main: string;
    dark: string;
    contrastText: string;
  }
}

const customPalette = {
  primary: {
    main: '#2379C2',
    light: '#E24C83',
    lighter: '#e4f1ff',
    dark: '#174b7c',
    contrastText: '#FFF',
  },
  secondary: {
    main: '#006DB8',
    light: '#338AC6',
    lighter: '#a1c7e4',
    dark: '#005BBB',
    contrastText: '#FFF',
  },
  error: {
    main: '#D12348',
    light: '#FF829D',
    lightest: '#FFE7EC',
    contrastText: '#FFF',
  },
  warning: {
    main: '#E56A2F',
    light: '#FFA579',
    lightest: '#FFDECE',
    contrastText: '#FFF',
  },
  success: {
    main: '#74B23B',
    light: '#C5EF9F',
    lighter: '#EBFFD8',
    contrastText: '#FFF',
  },
  black: {
    main: '#000000',
    light: '#202020',
  },
  link: {
    main: '#2878C1',
    darker: '#194C7B',
    light: '#83B8E8',
    lighter: '#E3F1FF',
    lightest: '#F9FCFF',
  },
  grey: {
    main: '#6A6A6A',
    light: '#A6A6A6',
    lighter: '#DBDBDB',
    lightest: '#F4F4F4',
  },
  kinder: {
    darker: '#C26714',
    main: '#FA9132',
    light: '#F2B984',
    lighter: '#FFF2E6',
  },
  kickKinder: {
    main: '#A46BBA',
    darker: '#68367C',
    light: '#E0A6F7',
    lighter: '#F8E6FF',
  },
  first: {
    main: '#FCE852',
    darker: '#C9AB36',
    light: '#FFF5AB',
    lighter: '#FFFDEB',
  },
  second: {
    main: '#27AFA7',
    darker: '#14605B',
    light: '#97E9E4',
    lighter: '#F3FFFE',
  },
  third: {
    main: '#FB8388',
    darker: '#B35054',
    light: '#FFA5A9',
    lighter: '#FFEEEE',
  },
  fourth: {
    main: '#5E90D8',
    darker: '#3C6092',
    light: '#9FC2F4',
    lighter: '#EDF5FF',
  },
};

const basePalette = createPalette({
  ...customPalette,
  grey: {
    50: '#F5F5F5',
    100: '#EAEAEA',
    400: '#A6A6A6',
    500: '#DBDBDB',
    600: '#6A6A6A',
    900: '#202020',
  },
});
export const FONT_GSS_PRINT_WIDE = 'HWT Print Wide';
export const FONT_GSS_PRINT_WIDE_BOLD = 'HWT Print Wide Bold';
export const FONT_GSS_PRINT_WIDE_BOOK = 'HWT Print Wide Book';
export const FONT_GSS_PRINT_WIDE_LIGHT = 'HWT Print Wide Light';
export const FONT_GSS_CURSIVE_BEG_WIDE = 'HWT Cursive Beginner Wide';
export const FONT_GSS_CURSIVE_BEG_BOLD = 'HWT Cursive Beginner Bold';
export const FONT_GSS_CURSIVE_BEG_BOOK = 'HWT Cursive Beginner Book';
export const FONT_GSS_CURSIVE_BEG_LIGHT = 'HWT Cursive Beginner Light';
export const FONT_GSS_FUTURA = 'MM Futura';
export const FONT_GSS_FUTURA_SEMI_BOLD = 'MM Futura Semi Bold';
export const FONT_GSS_FUTURA_SEMI_BOLD_ITALIC = 'MM Futura Semi Bold Italic';
export const FONT_GSS_FUTURA_BOOK = 'MM Futura Book';
export const FONT_GSS_FUTURA_BOOK_ITALIC = 'MM Futura Book Italic';
export const FONT_GSS_FUTURA_LIGHT_ITALIC = 'MM Futura Light Italic';
export const FONT_GSS_FUTURA_LIGHT = 'MM Futura Light';
export const FONT_GSS_FUTURA_BOLD = 'MM Futura Bold';
export const FONT_BILLY_BOLD = 'Billy Bold';
export const COLOR_BLACK = '#000000';
export const COLOR_WHITE = '#FFFFFF';

export const baseTheme = createTheme({
  // MVP only supporting (md lg)
  breakpoints: {
    values: {
      xs: 0,
      sm: 750,
      md: 1105,
      lg: 1490,
      xl: 1850,
    },
  },
  palette: basePalette,
  spacing: 4,
  shape: {
    borderRadius: 4,
  },
  constants: {
    // theme constants
    app: {
      footerOffset: STUDENT_APP_BUTTON_SIZES.default + STUDENT_APP_DEFAULT_PADDING.y,
      menuOffset: STUDENT_APP_BUTTON_SIZES.default + 2 * STUDENT_APP_DEFAULT_PADDING.y,
      paddingX: STUDENT_APP_DEFAULT_PADDING.x,
      paddingY: STUDENT_APP_DEFAULT_PADDING.y,
    },
    modals: {
      footerHeight: 80,
      stepperHeight: 24,
    },
  },
});

const theme: ThemeOptions = {
  ...baseTheme,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 'bold',
          '&:disabled': {
            opacity: DISABLED_OPACITY,
          },
        },
        outlinedPrimary: {
          backgroundColor: COLOR_WHITE,
          border: `2px solid ${basePalette.primary.main}`,
          color: `${basePalette.primary.main}`,
          '&:hover': {
            backgroundColor: COLOR_WHITE,
            border: `2px solid ${basePalette.primary.dark}`,
            color: `${basePalette.primary.dark}`,
          },
          '&:disabled': {
            border: `2px solid ${basePalette.grey[400]}`,
          },
        },
        contained: {
          borderRadius: '40px',
          minWidth: baseTheme.typography.pxToRem(STUDENT_APP_BUTTON_SIZES.default),
          height: baseTheme.typography.pxToRem(STUDENT_APP_BUTTON_SIZES.default),
          color: basePalette.primary.main,
          fontSize: baseTheme.typography.pxToRem(30),
          lineHeight: baseTheme.typography.pxToRem(42),
          fontFamily: FONT_GSS_FUTURA_BOLD,
          boxShadow: '0 0 0 0.5px #777777,0 4px 0 0 rgba(0,0,0,0.5);',
          padding: 0,
          border: '3.5px solid white',
          background: `linear-gradient(129.63deg, #F7FCFF 2.25%, #B9E1FF 100%)`,
          '&:hover': {
            background: `linear-gradient(129.63deg, #F7FCFF -40.25%, #B9E1FF 100%)`,
            boxShadow: '0 4px 0 0 rgba(0,0,0,0.5);',
          },
        },
        containedPrimary: {
          color: basePalette.primary.contrastText,
          background: `linear-gradient(140.64deg, #0094DF 0%, #005BBB 100%)`,
          '&:hover': {
            background: `linear-gradient(140.64deg, #0094DF -40%, #005BBB 100%)`,
          },
        },
        containedSecondary: {
          color: basePalette.primary.contrastText,
          background: `linear-gradient(136.6deg, #299452 0%, #0E7A11 100%)`,
          '&:hover': {
            color: '#0E7A11',
            background: `linear-gradient(136.6deg, #EAFFF2 -30%, #BFFFB8 100%)`,
          },
        },
        containedSizeSmall: {
          minWidth: baseTheme.typography.pxToRem(STUDENT_APP_BUTTON_SIZES.small),
          height: baseTheme.typography.pxToRem(STUDENT_APP_BUTTON_SIZES.small),
        },
        iconSizeMedium: {
          '& > *:first-child': {
            fontSize: baseTheme.typography.pxToRem(32),
          },
        },
      },
    },
  },

  typography: {
    fontFamily: FONT_GSS_FUTURA,
    //logged In user
    h1: {
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
      fontWeight: 'bold',
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(40),
      lineHeight: baseTheme.typography.pxToRem(60),
      '@media (max-width:800px)': {
        fontSize: baseTheme.typography.pxToRem(28),
        lineHeight: baseTheme.typography.pxToRem(40),
      },
    },
    // Star completedAssignmentsCount
    h2: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(28),
      lineHeight: baseTheme.typography.pxToRem(60),
      '@media (max-width:800px)': {
        fontSize: baseTheme.typography.pxToRem(24),
        lineHeight: baseTheme.typography.pxToRem(20),
      },
    },
    //welcome message
    h3: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 'bold',
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(18),
      lineHeight: baseTheme.typography.pxToRem(52.5),
      '@media (max-width:800px)': {
        fontSize: baseTheme.typography.pxToRem(14),
        lineHeight: baseTheme.typography.pxToRem(20),
      },
    },
    // Tutorial Messages
    h4: {
      fontFamily: FONT_GSS_FUTURA_BOOK,
      fontWeight: 'bold',
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(30),
      lineHeight: baseTheme.typography.pxToRem(45),
      '@media (max-width:800px)': {
        fontSize: baseTheme.typography.pxToRem(12),
        lineHeight: baseTheme.typography.pxToRem(20),
      },
    },
    // Home Screen Card Headers
    h5: {
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
      fontWeight: 'bold',
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(30),
      lineHeight: baseTheme.typography.pxToRem(45),
      '@media (max-width:800px)': {
        fontSize: baseTheme.typography.pxToRem(14),
        lineHeight: baseTheme.typography.pxToRem(20),
      },
    },
    // Assignment Message
    body1: {
      fontFamily: FONT_GSS_FUTURA_BOOK,
      fontWeight: 300,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(20),
      lineHeight: baseTheme.typography.pxToRem(30),
    },
    // Assignment Recomandation
    body2: {
      fontFamily: FONT_GSS_FUTURA_BOOK,
      fontWeight: 300,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(32),
      lineHeight: baseTheme.typography.pxToRem(35),
    },
    button: {
      textTransform: 'none',
    },
    subtitle1: {
      fontSize: baseTheme.typography.pxToRem(16),
      '@media (max-width:800px)': {
        fontSize: baseTheme.typography.pxToRem(14),
        lineHeight: baseTheme.typography.pxToRem(22),
      },
    },
    subtitle2: {
      fontFamily: FONT_GSS_FUTURA_BOLD,
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(24),
      lineHeight: baseTheme.typography.pxToRem(36),
      letterSpacing: baseTheme.typography.pxToRem(1),
    },
    caption: {
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: baseTheme.typography.pxToRem(16),
      lineHeight: baseTheme.typography.pxToRem(16),
    },
  },
};

export default createTheme(theme);
